import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/shared/components/ui/input";
import {
  Play,
  Square,
  RefreshCw,
  Wrench,
  ChevronRight,
  ChevronDown,
  ArrowRightToLine,
} from "lucide-react";
import { toast } from "sonner";
import { LogTerminal, LogTerminalRef } from "@/shared/components/ui/log-terminal";
import { getGatewayToolCalls, type ToolCallFeedItem } from "../services/serviceLogsService";

// Anonymized MCP tool-invocation feed (Gateway tab → "Tools" mode, PBI 81485).
// Layout: caller-session sidebar (org + user + connection type + OAuth app,
// newest activity first) + invocation feed for the selected session (or all).
// Live mode polls on a serialized timer; a date narrows to one day. User identity
// arrives already anonymized from the API; parameters carry only their key names.

const POLL_MS = 5000;
const FEED_TAIL = 200;

// Status são semânticos, então vêm dos tokens do contrato — ao contrário das
// cores de canal/tipo, que são identidade e seguem fixas de propósito.
const STATUS_CLASSES: Record<string, string> = {
  completed: "text-success-fg bg-success-bg",
  failed: "text-error-fg bg-error-bg",
  blocked: "text-error-fg bg-error-bg",
  pending: "text-warning-fg bg-warning-bg",
  processing: "text-info-fg bg-info-bg",
};

const CONNECTION_BADGES: Record<ToolCallFeedItem["connectionType"], { label: string; className: string }> = {
  oauth: { label: "OAuth", className: "text-violet-400 bg-violet-400/15" },
  api_token: { label: "API Token", className: "text-amber-400 bg-amber-400/15" },
  service: { label: "Service", className: "text-sky-400 bg-sky-400/15" },
  unknown: { label: "—", className: "text-gray-400 bg-gray-400/10" },
};

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

interface CallerSession {
  sessionKey: string;
  organizationName: string | null;
  productName: string | null;
  userHash: string;
  connectionType: ToolCallFeedItem["connectionType"];
  oauthClient: string | null;
  firstAt: string;
  lastAt: string;
  count: number;
  failedCount: number;
  lastTool: string;
}

// Groups feed items (newest first) into caller sessions ordered by latest activity.
const groupSessions = (items: ToolCallFeedItem[]): CallerSession[] => {
  const groups = new Map<string, CallerSession>();
  for (const item of items) {
    const failed = item.status === "failed" || item.status === "blocked" ? 1 : 0;
    const existing = groups.get(item.sessionKey);
    if (existing) {
      existing.count += 1;
      existing.failedCount += failed;
      // itens chegam do mais novo para o mais antigo — o último visto é o firstAt
      existing.firstAt = item.timestamp;
    } else {
      groups.set(item.sessionKey, {
        sessionKey: item.sessionKey,
        organizationName: item.organizationName,
        productName: item.productName,
        userHash: item.userHash,
        connectionType: item.connectionType,
        oauthClient: item.oauthClient,
        firstAt: item.timestamp,
        lastAt: item.timestamp,
        count: 1,
        failedCount: failed,
        lastTool: item.toolName,
      });
    }
  }
  return [...groups.values()];
};

// Compact range: same day → "8/11/2026, 10:01 → 10:37"; cross-day shows both dates.
const formatRange = (firstIso: string, lastIso: string): string => {
  const first = new Date(firstIso);
  const last = new Date(lastIso);
  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) return formatTime(lastIso);
  if (first.getTime() === last.getTime()) return last.toLocaleString();
  const timeOpts: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
  if (first.toDateString() === last.toDateString()) {
    return `${first.toLocaleDateString()}, ${first.toLocaleTimeString([], timeOpts)} → ${last.toLocaleTimeString([], timeOpts)}`;
  }
  return `${first.toLocaleString()} → ${last.toLocaleString()}`;
};

export const GatewayToolsFeed = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<ToolCallFeedItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [date, setDate] = useState<string>(""); // '' = live
  const [selectedSession, setSelectedSession] = useState<string | null>(null); // null = all
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Live-poll health: silent failures must not leave a green "live" badge while
  // the data is actually stale — surfaced as a non-intrusive indicator.
  const [pollFailed, setPollFailed] = useState(false);
  const logTerminalRef = useRef<LogTerminalRef>(null);
  const isPausedRef = useRef(isPaused);
  // Monotonic request sequence: a slow older response must not overwrite the
  // state written by a newer one (e.g. day switch racing the live poll).
  const requestSeqRef = useRef(0);

  const isLive = date === "";

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const load = useCallback(
    async (targetDate: string, silent = false) => {
      const requestSeq = ++requestSeqRef.current;
      if (!silent) setIsLoading(true);
      try {
        const response = await getGatewayToolCalls({
          date: targetDate || null,
          tail: FEED_TAIL,
        });
        // Every state update derived from this request stays behind the sequence
        // guard — a stale success must not clear a newer failure (and vice versa).
        if (requestSeq === requestSeqRef.current) {
          setItems(response.items ?? []);
          setPollFailed(false);
        }
      } catch {
        if (requestSeq === requestSeqRef.current) setPollFailed(true);
        if (!silent) toast.error(t("logs.tools.loadError", "Could not load the tool-call feed."));
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [t]
  );

  // Live mode: serialized recursive setTimeout (never overlapping requests).
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled) return;
      if (!isPausedRef.current) await load("", true);
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };

    // finally (não then): um erro no load inicial não pode matar o polling —
    // o load já trata o erro; aqui só garantimos que o loop continue.
    void load(date).finally(() => {
      if (isLive && !cancelled) timer = setTimeout(tick, POLL_MS);
    });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [date, isLive, load]);

  // Reset session selection when the day changes (session may not exist there).
  useEffect(() => {
    setSelectedSession(null);
    setExpandedIds(new Set());
    setExpandedGroups(new Set());
  }, [date]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (sessionKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(sessionKey)) next.delete(sessionKey);
      else next.add(sessionKey);
      return next;
    });
  };

  const sessions = useMemo(() => groupSessions(items), [items]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (selectedSession) {
      result = result.filter((item) => item.sessionKey === selectedSession);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.toolName.toLowerCase().includes(query) ||
          (item.organizationName ?? "").toLowerCase().includes(query) ||
          (item.productName ?? "").toLowerCase().includes(query) ||
          (item.oauthClient ?? "").toLowerCase().includes(query) ||
          item.connectionType.toLowerCase().includes(query) ||
          item.userHash.toLowerCase().includes(query) ||
          item.status.toLowerCase().includes(query) ||
          (item.paramsSummary ?? "").toLowerCase().includes(query)
      );
    }
    return result;
  }, [items, selectedSession, searchQuery]);

  // Sessão selecionada = chamadas em ordem cronológica (o feed chega do mais novo
  // para o mais antigo); "Todas as sessões" = grupos por chamador.
  const isSessionView = selectedSession !== null;

  const sessionItems = useMemo(
    () => (isSessionView ? [...filteredItems].reverse() : filteredItems),
    [isSessionView, filteredItems]
  );

  const groupedFeed = useMemo(() => {
    if (isSessionView) return [];
    const map = new Map<string, ToolCallFeedItem[]>();
    for (const item of filteredItems) {
      const arr = map.get(item.sessionKey) ?? [];
      arr.push(item);
      map.set(item.sessionKey, arr);
    }
    return [...map.entries()].map(([sessionKey, groupItems]) => ({
      sessionKey,
      items: [...groupItems].reverse(),
    }));
  }, [isSessionView, filteredItems]);

  // Abrir uma sessão é navegação explícita: força o scroll mesmo que o usuário
  // tenha rolado para cima na visão anterior (senão a sessão abre no meio).
  useEffect(() => {
    if (!isSessionView) return;
    const timeoutId = setTimeout(() => logTerminalRef.current?.forceScrollToBottom(), 100);
    return () => clearTimeout(timeoutId);
  }, [isSessionView, selectedSession]);

  // Chamadas novas na sessão aberta acompanham o fim apenas se o usuário não
  // tiver rolado para cima para ler algo.
  useEffect(() => {
    if (!isSessionView) return;
    const timeoutId = setTimeout(() => logTerminalRef.current?.scrollToBottom(), 100);
    return () => clearTimeout(timeoutId);
  }, [isSessionView, sessionItems.length]);

  const renderSessionBlock = (session: CallerSession) => {
    const connBadge = CONNECTION_BADGES[session.connectionType];
    const isSelected = selectedSession === session.sessionKey;
    return (
      <button
        key={session.sessionKey}
        onClick={() => setSelectedSession(isSelected ? null : session.sessionKey)}
        className={`w-full text-left rounded-md border p-2 space-y-1 transition-colors ${
          isSelected
            ? "border-primary bg-primary/10"
            : "border-border hover:bg-muted/60"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span
            className={`${connBadge.className} font-mono text-[10px] font-medium px-1.5 py-0.5 rounded`}
          >
            {connBadge.label}
          </span>
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
            {session.count} {t("logs.tools.calls", "calls")}
            {session.failedCount > 0 && (
              <span className="text-red-400 ml-1">
                · {session.failedCount} {t("logs.tools.failed", "falhas")}
              </span>
            )}
          </span>
        </div>
        {session.organizationName && (
          <div className="text-xs font-medium truncate">{session.organizationName}</div>
        )}
        {session.productName && (
          <div className="text-xs text-muted-foreground truncate">{session.productName}</div>
        )}
        {session.oauthClient && (
          <div className="text-[10px] text-violet-400 font-mono truncate">
            {session.oauthClient}
          </div>
        )}
        <div className="text-[10px] text-muted-foreground font-mono truncate">
          {session.userHash}
        </div>
        <div className="text-[10px] text-muted-foreground truncate">
          {t("logs.tools.lastTool", "Última tool")}:{" "}
          <span className="font-mono text-emerald-500/80">{session.lastTool}</span>
        </div>
        <div className="text-[10px] text-muted-foreground">
          {formatRange(session.firstAt, session.lastAt)}
        </div>
      </button>
    );
  };

  // Linha de chamada minimalista — metadados do chamador vivem no cabeçalho do
  // grupo (ou no card da sidebar quando a sessão está selecionada). Mesmo padrão
  // de expansão do Chats: contraído = só a linha; expandido = detalhes (parâmetros,
  // erro, retorno) indentados sob um fio à esquerda.
  const renderCallRow = (item: ToolCallFeedItem) => {
    const statusClass = STATUS_CLASSES[item.status] ?? "text-gray-400 bg-gray-400/10";
    const hasDetails = !!(item.paramsSummary || item.errorMessage || item.resultPreview);
    const isExpanded = expandedIds.has(item.id);
    return (
      <div key={item.id} className="py-0.5">
        <button
          onClick={() => hasDetails && toggleExpanded(item.id)}
          className={`flex items-center gap-2 flex-wrap text-xs w-full text-left ${
            hasDetails ? "" : "cursor-default"
          }`}
          title={
            hasDetails
              ? isExpanded
                ? t("logs.chats.collapse", "Contrair")
                : t("logs.chats.expand", "Expandir")
              : undefined
          }
        >
          {hasDetails ? (
            <span className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 shrink-0">
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </span>
          ) : (
            <span className="w-3.5 shrink-0" />
          )}
          <span className="text-gray-500">[{formatTime(item.timestamp)}]</span>
          <span className="text-emerald-600 dark:text-emerald-300 font-mono font-medium">
            {item.toolName}
          </span>
          <span
            className={`${statusClass} font-mono text-[10px] font-medium px-1.5 py-0.5 rounded`}
          >
            {item.status}
          </span>
        </button>
        {isExpanded && (
          <div className="mt-1 ml-5 border-l border-hairline pl-3 space-y-1">
            {item.paramsSummary && (
              <div className="text-muted-foreground text-xs">
                {t("logs.tools.params", "Parâmetros")}: {item.paramsSummary}
              </div>
            )}
            {item.errorMessage && (
              <div className="text-red-500 dark:text-red-400 text-xs whitespace-pre-wrap break-words">
                {item.errorMessage}
              </div>
            )}
            {item.resultPreview && (
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
                  {t("logs.tools.resultPreview", "Retorno (trecho)")}
                </div>
                <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                  {item.resultPreview}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder={t("logs.tools.searchPlaceholder", "Buscar nas chamadas de ferramentas...")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 max-w-md min-w-[180px]"
        />
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {t("logs.dateSelector", "Data")}:
          </span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 text-sm px-3 rounded-md border border-input bg-background"
          />
          {!isLive && (
            <button onClick={() => setDate("")} className="text-sm text-primary hover:underline">
              {t("logs.liveOption", "Tempo real (hoje)")}
            </button>
          )}
        </div>
        <span className="text-sm">
          {isLive ? (
            isPaused ? (
              <span className="flex items-center text-yellow-500">
                <span className="h-2 w-2 rounded-full bg-yellow-500 mr-2"></span>
                {t("logs.paused", "Pausado")}
              </span>
            ) : pollFailed ? (
              <span className="flex items-center text-red-500">
                <span className="h-2 w-2 rounded-full bg-red-500 mr-2"></span>
                {t("logs.liveUpdatesFailing", "Tempo real com falhas — dados podem estar desatualizados")}
              </span>
            ) : (
              <span className="flex items-center text-green-500">
                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse mr-2"></span>
                {t("logs.tools.liveFeed", "Feed em tempo real")}
              </span>
            )
          ) : (
            <span className="flex items-center text-blue-400">
              <span className="h-2 w-2 rounded-full bg-blue-400 mr-2"></span>
              {t("logs.historicalSnapshot", "Snapshot histórico — sem atualização em tempo real")}
            </span>
          )}
        </span>
        {filteredItems.length > 0 && (
          <span className="text-sm text-muted-foreground">
            {t("logs.tools.itemCount", { count: filteredItems.length })}
          </span>
        )}
      </div>

      <div className="flex gap-4 h-[500px]">
        {/* Caller-session sidebar */}
        <div className="w-60 shrink-0 flex flex-col border rounded-md bg-card overflow-hidden">
          <div className="px-3 py-2 border-b flex items-center gap-2 text-sm font-medium">
            <Wrench className="h-4 w-4" />
            {t("logs.tools.sessions", "Sessões")}
            <span className="ml-auto text-xs text-muted-foreground">{sessions.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            <button
              onClick={() => setSelectedSession(null)}
              className={`w-full text-left rounded-md border p-2 text-sm transition-colors ${
                selectedSession === null
                  ? "border-primary bg-primary/10 font-medium"
                  : "border-border hover:bg-muted/60"
              }`}
            >
              {t("logs.tools.allSessions", "Todas as sessões")}
            </button>
            {sessions.map(renderSessionBlock)}
            {sessions.length === 0 && !isLoading && (
              <p className="text-xs text-muted-foreground px-1 py-2">
                {t("logs.tools.noSessions", "Nenhuma sessão no período.")}
              </p>
            )}
          </div>
        </div>

        {/* Invocation feed */}
        <div className="flex-1 overflow-hidden">
          {filteredItems.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center h-full border rounded-md bg-card text-muted-foreground">
              <p>{t("logs.tools.empty", "Nenhuma chamada de ferramenta no período selecionado.")}</p>
            </div>
          ) : (
            <LogTerminal
              className="h-full"
              ref={logTerminalRef}
              title={t("logs.tools.terminalTitle", "Chamadas de ferramentas MCP (Gateway)")}
              actionButtons={
                <div className="flex space-x-3">
                  {isLive &&
                    (isPaused ? (
                      <button
                        onClick={() => setIsPaused(false)}
                        className="text-green-500 hover:text-green-700 focus:outline-none"
                        title={t("logs.resume", "Retomar")}
                      >
                        <Play className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsPaused(true)}
                        className="text-yellow-500 hover:text-yellow-700 focus:outline-none"
                        title={t("logs.pause", "Pausar")}
                      >
                        <Square className="h-4 w-4" />
                      </button>
                    ))}
                  <button
                    onClick={() => load(date)}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    title={t("logs.tools.refresh", "Atualizar")}
                  >
                    <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                  </button>
                </div>
              }
            >
              {isSessionView
                ? // Sessão selecionada — chamadas em ordem cronológica; metadados do
                  // chamador ficam no card da sidebar.
                  sessionItems.map((item) => renderCallRow(item))
                : // "Todas as sessões" — acordeão por chamador: cabeçalho expande/retrai
                  // as chamadas inline; o botão à direita abre a visão dedicada.
                  groupedFeed.map((group) => {
                    const head = group.items[group.items.length - 1]; // mais recente
                    const first = group.items[0];
                    const connBadge = CONNECTION_BADGES[head.connectionType];
                    const groupExpanded = expandedGroups.has(group.sessionKey);
                    return (
                      <div
                        key={group.sessionKey}
                        className="py-2 border-b border-hairline last:border-b-0"
                      >
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleGroup(group.sessionKey)}
                            className="flex items-center gap-2 flex-wrap text-xs flex-1 min-w-0 text-left hover:bg-gray-200/40 dark:hover:bg-white/5 rounded px-1 py-0.5"
                            title={
                              groupExpanded
                                ? t("logs.chats.collapse", "Contrair")
                                : t("logs.chats.expand", "Expandir")
                            }
                          >
                            <span className="text-gray-500 shrink-0">
                              {groupExpanded ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </span>
                            <span
                              className={`${connBadge.className} font-mono text-[10px] font-medium px-1.5 py-0.5 rounded`}
                            >
                              {connBadge.label}
                            </span>
                            {head.oauthClient && (
                              <span className="text-violet-500 dark:text-violet-400 font-mono">
                                {head.oauthClient}
                              </span>
                            )}
                            {head.organizationName && (
                              <span className="text-sky-600 dark:text-sky-400">
                                {head.organizationName}
                              </span>
                            )}
                            {head.productName && (
                              <span className="text-teal-600 dark:text-teal-400">
                                {head.productName}
                              </span>
                            )}
                            <span className="text-gray-500 font-mono">{head.userHash}</span>
                            <span className="text-gray-500 font-mono text-[10px]">
                              {group.sessionKey}
                            </span>
                            <span className="ml-auto text-gray-500 text-[10px] whitespace-nowrap">
                              {formatRange(first.timestamp, head.timestamp)} · {group.items.length}{" "}
                              {t("logs.tools.calls", "calls")}
                            </span>
                          </button>
                          <button
                            onClick={() => setSelectedSession(group.sessionKey)}
                            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none shrink-0 px-1"
                            title={t("logs.tools.openSession", "Abrir sessão")}
                          >
                            <ArrowRightToLine className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        {groupExpanded && (
                          <div className="mt-1 ml-5 border-l border-hairline pl-2 space-y-1">
                            {group.items.map((item) => renderCallRow(item))}
                          </div>
                        )}
                      </div>
                    );
                  })}
            </LogTerminal>
          )}
        </div>
      </div>
    </div>
  );
};
